import React, { useEffect, useCallback } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Grid, 
  Typography, 
  CircularProgress, 
  LinearProgress,
  Button, 
  Tooltip,
  Chip,
  alpha,
  useTheme,
  Avatar,
  useMediaQuery
} from '@mui/material';
import { 
  Balance as BalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Savings as SavingsIcon,
  AttachMoney as AttachMoneyIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useBudget } from '../../contexts/BudgetContext';
import { useLoading } from '../../contexts/LoadingContext';
import { useNavigation } from '../../contexts/NavigationContext';
import type { Change } from '../../contexts/NavigationContext';
import { budgets } from '../../services/api';

const MonthlySummary: React.FC = () => {
  const { currentBudget, setCurrentBudget, formatCurrency } = useBudget();
  const { isLoading } = useLoading();
  const { setChangeHistory, setUndoneChanges } = useNavigation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  
  const summaryData = React.useMemo(() => {
    if (!currentBudget) return null;
    
    const totalIncome = currentBudget.categories
      .filter(cat => cat.type === 'income')
      .reduce((sum, cat) => sum + cat.actualAmount, 0);
    
    const totalExpenses = currentBudget.categories
      .filter(cat => cat.type === 'expense')
      .reduce((sum, cat) => sum + cat.actualAmount, 0);

    const totalSavings = currentBudget.categories
      .filter(cat => cat.type === 'savings')
      .reduce((sum, cat) => sum + cat.actualAmount, 0);

    const netIncome = totalIncome - totalExpenses - totalSavings;
    
    // "To be budgeted" = total income + rollover from previous months - amounts assigned to categories
    // This creates a proper envelope budgeting system where unassigned income rolls over
    const rolloverAmount = currentBudget.toBeBudgetedRollover || 0;
    const toBeBudgeted = totalIncome + rolloverAmount - currentBudget.totalBudgeted;

    return { totalIncome, totalExpenses, totalSavings, netIncome, toBeBudgeted };
  }, [currentBudget]);

  const handleBalanceAll = useCallback(async () => {
    if (!currentBudget || !summaryData || summaryData.toBeBudgeted <= 0) return;

    let remainingToBeBudgeted = summaryData.toBeBudgeted;
    const updatedCategories = [...currentBudget.categories];
    const changes: Change[] = [];

    // Get expense and savings categories that are over budget, maintaining their original order
    const categoriesNeedingFunds = updatedCategories
      .filter(cat => (cat.type === 'expense' || cat.type === 'savings') && cat.actualAmount > cat.budgetedAmount);

    // Distribute available funds to categories with deficits in list order
    for (const category of categoriesNeedingFunds) {
      if (remainingToBeBudgeted <= 0) break;

      const deficit = category.actualAmount - category.budgetedAmount;
      const amountToAdd = Math.min(deficit, remainingToBeBudgeted);
      
      if (amountToAdd <= 0) continue;

      const categoryIndex = updatedCategories.findIndex(
        cat => cat.name === category.name && cat.type === category.type
      );

      const oldAmount = updatedCategories[categoryIndex].budgetedAmount;
      const newAmount = oldAmount + amountToAdd;

      updatedCategories[categoryIndex] = {
        ...updatedCategories[categoryIndex],
        budgetedAmount: newAmount
      };

      changes.push({
        type: 'budget_amount',
        categoryName: category.name,
        categoryType: category.type,
        oldAmount,
        newAmount,
        timestamp: Date.now()
      });

      remainingToBeBudgeted -= amountToAdd;
    }

    // Calculate the new total budgeted amount
    const newTotalBudgeted = currentBudget.totalBudgeted + (summaryData.toBeBudgeted - remainingToBeBudgeted);

    // Update the budget with the new category amounts
    const updatedBudget = {
      ...currentBudget,
      categories: updatedCategories,
      totalBudgeted: newTotalBudgeted
    };

    try {
      // Save to database
      await budgets.update(currentBudget._id, {
        categories: updatedCategories,
        totalBudgeted: newTotalBudgeted
      });

      // Update local state only after successful save
      setCurrentBudget(updatedBudget);
      setChangeHistory(prev => [...prev, ...changes]);
      setUndoneChanges([]);
    } catch (error) {
      console.error('Error updating budget:', error);
    }
  }, [currentBudget, summaryData, setCurrentBudget, setChangeHistory, setUndoneChanges]);

  // Add keyboard shortcut listener
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+B
      if (event.ctrlKey && event.key.toLowerCase() === 'b') {
        event.preventDefault(); // Prevent the browser's default behavior
        if (summaryData?.toBeBudgeted && summaryData.toBeBudgeted > 0) {
          handleBalanceAll();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [summaryData?.toBeBudgeted, handleBalanceAll]);

  // Calculate detailed progress metrics
  const progressMetrics = React.useMemo(() => {
    if (!currentBudget) return null;
    
    const expenseCategories = currentBudget.categories.filter(cat => cat.type === 'expense');
    const savingsCategories = currentBudget.categories.filter(cat => cat.type === 'savings');
    
    // Calculate expense progress
    const expensesOnTrack = expenseCategories.filter(cat => cat.actualAmount <= cat.budgetedAmount).length;
    const expenseProgress = expenseCategories.length > 0 ? (expensesOnTrack / expenseCategories.length) * 100 : 100;
    
    // Calculate savings progress - count categories that have met their goals
    const savingsOnTrack = savingsCategories.filter(cat => cat.actualAmount >= cat.budgetedAmount).length;
    const savingsProgress = savingsCategories.length > 0 ? (savingsOnTrack / savingsCategories.length) * 100 : 100;
    
    // Overall budget health
    const totalBudgetedExpenses = expenseCategories.reduce((sum, cat) => sum + cat.budgetedAmount, 0);
    const totalActualExpenses = expenseCategories.reduce((sum, cat) => sum + cat.actualAmount, 0);
    const expenseEfficiency = totalBudgetedExpenses > 0 ? ((totalBudgetedExpenses - totalActualExpenses) / totalBudgetedExpenses) * 100 : 0;
    
    return {
      expenseProgress,
      savingsProgress,
      expenseEfficiency: Math.max(0, expenseEfficiency),
      totalCategories: expenseCategories.length + savingsCategories.length,
      categoriesOnTrack: expensesOnTrack + savingsOnTrack
    };
  }, [currentBudget]);

  const content = React.useMemo(() => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      );
    }

    if (!currentBudget || !summaryData) return null;

    const isNetPositive = summaryData.netIncome >= 0;
    const needsBudgeting = summaryData.toBeBudgeted > 0;
    const isOverBudget = summaryData.toBeBudgeted < 0;

    const cardVariants = {
      hidden: { opacity: 0, y: 20 },
      visible: { opacity: 1, y: 0 }
    };

    return (
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 }, alignItems: 'stretch' }}>
        {/* Header Section */}
        <Grid item xs={12}>
          <Box 
            display="flex" 
            flexDirection={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between" 
            alignItems={{ xs: 'stretch', sm: 'center' }}
            gap={{ xs: 2, sm: 0 }}
            mb={2}
          >
            <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }}>
              {!isSmall && (
                <Avatar 
                  sx={{ 
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 }
                  }}
                >
                  <AccountBalanceIcon fontSize={isSmall ? 'small' : 'medium'} />
                </Avatar>
              )}
              <Box>
                <Typography 
                  variant={isSmall ? "h6" : "h5"} 
                  fontWeight={600}
                  sx={{ fontSize: { xs: '1.125rem', sm: '1.5rem' } }}
                >
                  Monthly Overview
                </Typography>
                {needsBudgeting && (
                  <Chip
                    label={`${formatCurrency(summaryData.toBeBudgeted)} to allocate`}
                    color="warning"
                    size="small"
                    icon={<WarningIcon fontSize="small" />}
                    sx={{ 
                      mt: 0.5,
                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                      height: { xs: 24, sm: 28 }
                    }}
                  />
                )}
              </Box>
            </Box>
            {summaryData.toBeBudgeted > 0 && (
              <Tooltip title="Automatically balance over-budget categories (Ctrl+B)">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    startIcon={<BalanceIcon fontSize={isMobile ? 'small' : 'medium'} />}
                    onClick={handleBalanceAll}
                    size={isMobile ? "small" : "medium"}
                    variant="contained"
                    fullWidth={isSmall}
                    sx={{
                      borderRadius: 2,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      px: { xs: 2, sm: 3 },
                      py: { xs: 1, sm: 1.5 },
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      boxShadow: `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.3)}`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                        boxShadow: `0 6px 20px 0 ${alpha(theme.palette.primary.main, 0.4)}`,
                      }
                    }}
                  >
                    {isSmall ? 'Balance' : 'Balance All'}
                  </Button>
                </motion.div>
              </Tooltip>
            )}
          </Box>
        </Grid>

        {/* Summary Cards */}
        <Grid item xs={12} sm={6} lg={3} sx={{ display: 'flex' }}>
          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{ width: '100%', height: '100%' }}
          >
            <Card 
              sx={{ 
                height: '100%',
                minHeight: { xs: '180px', sm: '200px', lg: '220px' },
                background: `linear-gradient(135deg, ${
                  isNetPositive 
                    ? `${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.light, 0.05)}`
                    : `${alpha(theme.palette.error.main, 0.1)}, ${alpha(theme.palette.error.light, 0.05)}`
                })`,
                border: `2px solid ${alpha(
                  isNetPositive ? theme.palette.success.main : theme.palette.error.main, 
                  0.2
                )}`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${
                    isNetPositive ? theme.palette.success.main : theme.palette.error.main
                  }, ${
                    isNetPositive ? theme.palette.success.light : theme.palette.error.light
                  })`,
                }
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }} mb={2}>
                  <Avatar 
                    sx={{ 
                      bgcolor: alpha(
                        isNetPositive ? theme.palette.success.main : theme.palette.error.main, 
                        0.15
                      ),
                      color: isNetPositive ? theme.palette.success.main : theme.palette.error.main,
                      width: { xs: 40, sm: 48 },
                      height: { xs: 40, sm: 48 }
                    }}
                  >
                    {isNetPositive ? <TrendingUpIcon /> : <TrendingDownIcon />}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={500} color="text.secondary">
                      Net Income
                    </Typography>
                    <Chip
                      label={isNetPositive ? "Positive" : "Negative"}
                      color={isNetPositive ? "success" : "error"}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>
                <Box flex={1} display="flex" flexDirection="column" justifyContent="center">
                  <Typography 
                    variant={isMobile ? "h5" : "h4"} 
                    fontWeight={700}
                    color={isNetPositive ? 'success.main' : 'error.main'}
                    sx={{ 
                      mb: 1,
                      fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
                    }}
                  >
                    {formatCurrency(summaryData.netIncome)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isNetPositive 
                      ? "Your finances are on track this month!" 
                      : "Consider reviewing your spending categories."
                    }
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} lg={3} sx={{ display: 'flex' }}>
          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{ width: '100%', height: '100%' }}
          >
            <Card 
              sx={{ 
                height: '100%',
                minHeight: { xs: '180px', sm: '200px', lg: '220px' },
                background: `linear-gradient(135deg, ${
                  isOverBudget
                    ? `${alpha(theme.palette.warning.main, 0.1)}, ${alpha(theme.palette.warning.light, 0.05)}`
                    : needsBudgeting
                      ? `${alpha(theme.palette.info.main, 0.1)}, ${alpha(theme.palette.info.light, 0.05)}`
                      : `${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.light, 0.05)}`
                })`,
                border: `2px solid ${alpha(
                  isOverBudget 
                    ? theme.palette.warning.main 
                    : needsBudgeting 
                      ? theme.palette.info.main 
                      : theme.palette.success.main, 
                  0.2
                )}`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${
                    isOverBudget 
                      ? theme.palette.warning.main 
                      : needsBudgeting 
                        ? theme.palette.info.main 
                        : theme.palette.success.main
                  }, ${
                    isOverBudget 
                      ? theme.palette.warning.light 
                      : needsBudgeting 
                        ? theme.palette.info.light 
                        : theme.palette.success.light
                  })`,
                }
              }}
            >
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Avatar 
                    sx={{ 
                      bgcolor: alpha(
                        isOverBudget 
                          ? theme.palette.warning.main 
                          : needsBudgeting 
                            ? theme.palette.info.main 
                            : theme.palette.success.main, 
                        0.15
                      ),
                      color: isOverBudget 
                        ? theme.palette.warning.main 
                        : needsBudgeting 
                          ? theme.palette.info.main 
                          : theme.palette.success.main,
                      width: 48,
                      height: 48
                    }}
                  >
                    {isOverBudget ? <WarningIcon /> : needsBudgeting ? <AttachMoneyIcon /> : <SavingsIcon />}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={500} color="text.secondary">
                      To Be Budgeted
                    </Typography>
                    <Chip
                      label={
                        isOverBudget 
                          ? "Over Budget" 
                          : needsBudgeting 
                            ? "Needs Allocation" 
                            : "All Allocated"
                      }
                      color={
                        isOverBudget 
                          ? "warning" 
                          : needsBudgeting 
                            ? "info" 
                            : "success"
                      }
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>
                <Box flex={1} display="flex" flexDirection="column" justifyContent="center">
                  <Typography 
                    variant="h4" 
                    fontWeight={700}
                    color={
                      isOverBudget 
                        ? 'warning.main' 
                        : needsBudgeting 
                          ? 'info.main' 
                          : 'success.main'
                    }
                    sx={{ mb: 1 }}
                  >
                    {formatCurrency(Math.abs(summaryData.toBeBudgeted))}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isOverBudget 
                      ? "You've allocated more than available income."
                      : needsBudgeting 
                        ? "Assign this money to your budget categories."
                        : "Perfect! All your income has been allocated."
                    }
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Savings Rate Card */}
        <Grid item xs={12} sm={6} lg={3} sx={{ display: 'flex' }}>
          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.5, delay: 0.3 }}
            style={{ width: '100%', height: '100%' }}
          >
            <Card 
              sx={{ 
                height: '100%',
                minHeight: '220px',
                background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.light, 0.05)})`,
                border: `2px solid ${alpha(theme.palette.success.main, 0.2)}`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
                }
              }}
            >
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Avatar 
                    sx={{ 
                      bgcolor: alpha(theme.palette.success.main, 0.15),
                      color: theme.palette.success.main,
                      width: 48,
                      height: 48
                    }}
                  >
                    <SavingsIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={500} color="text.secondary">
                      Savings Rate
                    </Typography>
                  </Box>
                </Box>
                
                <Box flex={1} display="flex" flexDirection="column" justifyContent="center">
                  <Typography 
                    variant="h4" 
                    fontWeight={700}
                    color="success.main"
                    sx={{ mb: 1 }}
                  >
                    {summaryData.totalIncome > 0 ? Math.round((summaryData.totalSavings / summaryData.totalIncome) * 100) : 0}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {summaryData.totalSavings > 0 
                      ? summaryData.totalSavings / summaryData.totalIncome >= 0.2
                        ? "Excellent savings rate! 🌟" 
                        : summaryData.totalSavings / summaryData.totalIncome >= 0.1
                          ? "Good progress on savings! 💪"
                          : "Building your savings habit! 📈"
                      : "Start your savings journey today! 🎯"
                    }
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Budget Progress Card */}
        <Grid item xs={12} sm={6} lg={3} sx={{ display: 'flex' }}>
          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.5, delay: 0.4 }}
            style={{ width: '100%', height: '100%' }}
          >
            <Card 
              sx={{ 
                height: '100%',
                minHeight: '220px',
                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)}, ${alpha(theme.palette.secondary.light, 0.05)})`,
                border: `2px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`,
                }
              }}
            >
              <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Avatar 
                    sx={{ 
                      bgcolor: alpha(theme.palette.secondary.main, 0.15),
                      color: theme.palette.secondary.main,
                      width: 48,
                      height: 48
                    }}
                  >
                    <TrendingUpIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={500} color="text.secondary">
                      Budget Progress
                    </Typography>
                  </Box>
                </Box>
                
                {progressMetrics && (
                  <Box flex={1} display="flex" flexDirection="column" justifyContent="center" gap={2}>
                    <Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="text.secondary">
                          Expense Control
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="primary.main">
                          {Math.round(progressMetrics.expenseProgress)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={progressMetrics.expenseProgress}
                        color="primary"
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                    
                    <Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="text.secondary">
                          Savings Goals
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          {Math.round(progressMetrics.savingsProgress)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={progressMetrics.savingsProgress}
                        color="success"
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>
                    
                    <Box textAlign="center" mt={1}>
                      <Typography variant="caption" color="text.secondary">
                        {progressMetrics.categoriesOnTrack} of {progressMetrics.totalCategories} categories on track
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>
    );
  }, [isLoading, currentBudget, summaryData, formatCurrency, handleBalanceAll, theme]);

  return content;
};

export default MonthlySummary; 